import i18n from "../../i18n";
import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import { IconPencil, IconTrash } from "@tabler/icons";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
  PageList,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import ImagePicker from "../../components/imagePicker";
import UniqueTextInput from "../../components/uniqueTextInput";
import { isCompanyActive } from "../../helpers/companyWorkspace";
import { useServerApi } from "../../hooks/userServerApi";

export const STAMP_DEFAULT_COMPANY_EXPLANATION =
  "If this page has no company, the system works like a single corporation and older debit notes, renewals, and quotations stay visible. After you add the first company, run Attach existing records so those older rows are assigned to the default workspace. Inspal import already creates at least one company. Safe to run more than once.";

const apiEntity = "company";

const tableSchema = {
  showActionColumn: true,
  searchableFields: ["name", "code", "cname", "shortName", "email"],
  title: "Companies",
  apiEntity,
  columns: [
    {
      field: "code",
      headerName: "Company Code",
    },
    {
      field: "name",
      headerName: "Name",
    },
    {
      field: "cname",
      headerName: "Chinese Name",
    },
    {
      field: "active",
      headerName: "Active",
      cellRender: (col, data) => {
        const active = isCompanyActive(data);
        return (
          <Badge size="xs" color={active ? "green" : "gray"}>
            {active ? "Yes" : "No"}
          </Badge>
        );
      },
    },
    {
      field: "isDefault",
      headerName: "Default",
      cellRender: (col, data) =>
        data.isDefault ? (
          <Badge size="xs" color="blue">
            Yes
          </Badge>
        ) : (
          <Badge size="xs" color="gray">
            No
          </Badge>
        ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      cellRender: (col, data) =>
        moment(data.createdAt).format("YYYY-MM-DD HH:mm"),
    },
    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
          props: {},
        },
      ],
      actionMenu: [
        {
          name: "delete",
          label: "Delete data",
          icon: IconTrash,
          props: {
            color: "red",
          },
        },
      ],
    },
  ],
};

const formSchema = {
  title: "Company",
  titleField: "code",
  apiEntity,
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl",
  },
  initialValues: {
    code: "",
    name: "",
    cname: "",
    shortName: "",
    address: "",
    caddress: "",
    email: "",
    tel: "",
    fax: "",
    website: "",
    image: "",
    active: true,
    isDefault: false,
  },
  layout: {
    containers: [
      {
        key: "basic-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          {
            key: "basic-g1",
            props: {
              span: 9,
              mt: 0,
            },
          },
          {
            key: "basic-g2",
            props: {
              span: 3,
            },
          },
        ],
      },
      {
        key: "section1",
        parent: "basic-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic Info",
          description: "Please fill in details",
        },
      },
      {
        key: "section4",
        parent: "basic-g2",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Company Logo",
        },
      },
      {
        key: "section2",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Contact Info",
          description: "Please fill in details",
        },
      },
      {
        key: "section3",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Address",
          description: "Please fill in details",
        },
      },
    ],
    fields: [
      {
        name: "image",
        component: ImagePicker,
        parent: "section4",
        props: {
          size: 160,
          updateAndSave: false,
        },
      },
      {
        name: "code",
        component: UniqueTextInput,
        parent: "section1",
        props: {
          required: true,
          uniqueEntity: "company",
          errorMessage: "Company Code is already exist.",
          label: "Company Code",
        },
      },
      {
        name: "name",
        component: TextInput,
        parent: "section1",
        props: {
          required: true,
          label: "English Name",
        },
      },
      {
        name: "cname",
        component: TextInput,
        parent: "section1",
        props: {
          label: "Chinese Name",
        },
      },
      {
        name: "shortName",
        component: TextInput,
        parent: "section1",
        props: {
          label: "Short Name",
        },
      },
      {
        name: "active",
        component: Switch,
        parent: "section1",
        props: {
          label: "Active",
          type: "checkbox",
        },
      },
      {
        name: "isDefault",
        component: Switch,
        parent: "section1",
        props: {
          label: "Default workspace",
          type: "checkbox",
        },
      },
      {
        name: "website",
        component: TextInput,
        parent: "section2",
        props: {
          label: "Website",
        },
      },
      {
        name: "email",
        component: TextInput,
        parent: "section2",
        props: {
          label: "Email",
        },
      },
      {
        name: "tel",
        component: TextInput,
        parent: "section2",
        props: {
          label: "Tel.",
        },
      },
      {
        name: "fax",
        component: TextInput,
        parent: "section2",
        props: {
          label: "Fax",
        },
      },
      {
        name: "address",
        component: TextInput,
        parent: "section3",
        props: {
          label: "Address",
        },
      },
      {
        name: "caddress",
        component: TextInput,
        parent: "section3",
        props: {
          label: "Address (Chinese)",
        },
      },
    ],
  },
};

const Company = () => {
  const [api] = useServerApi();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stamping, setStamping] = useState(false);

  const customActions = useMemo(
    () => [
      {
        label: "Attach existing records",
        variant: "default",
        requiresWrite: true,
        onClick: () => setConfirmOpen(true),
      },
    ],
    []
  );

  const handleStamp = async () => {
    setStamping(true);
    try {
      const result = await api.Company.stampDefaultCompany();
      if (!result) {
        showNotification({
          color: "red",
          title: "Attach failed",
          message: "Could not attach existing records.",
        });
        return;
      }
      showNotification({
        color: result.skipped ? "yellow" : "green",
        title: result.title || "Done",
        message: result.message,
      });
      setConfirmOpen(false);
    } catch (error) {
      showNotification({
        color: "red",
        title: "Attach failed",
        message: String(error?.message || error),
      });
    } finally {
      setStamping(false);
    }
  };

  return (
    <>
      <Alert mb="md" color="blue" title="When to attach existing records">
        {STAMP_DEFAULT_COMPANY_EXPLANATION}
      </Alert>
      <PageList
        title={"Companies"}
        apiEntity={apiEntity}
        tableSchema={tableSchema}
        formSchema={formSchema}
        showImport={false}
        customActions={customActions}
      />
      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Attach existing records"
        size="md"
      >
        <Text size="sm" mb="md">
          {STAMP_DEFAULT_COMPANY_EXPLANATION}
        </Text>
        <Text size="sm" color="dimmed" mb="md">
          This assigns debit notes, orders, renewals, quotations, client
          payments, GI settlements, and payroll records that have no company to
          the default workspace (or the first company).
        </Text>
        <Group position="right">
          <Button variant="default" onClick={() => setConfirmOpen(false)}>{i18n.t("Cancel")}</Button>
          <Button loading={stamping} onClick={handleStamp}>
            Attach to default workspace
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default Company;
